import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { csAdminApi } from '../../../services/api';
import { useLookups } from '../../../hooks/useLookups';
import {
  DEFAULT_MANDATORY_TYPE_CODES,
  DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES,
  lookupLabel,
  resolveDirectorMandatoryTypeCodes,
  resolveMandatoryTypeCodes,
} from '../../../utils/mandatoryDocumentTypes';
import { useSnackbar } from 'notistack';
import {
  Paper,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  Tabs,
  Tab,
  Switch,
  Divider,
  IconButton,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Avatar,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  NotificationsOutlined as NotificationsIcon,
  SecurityOutlined as SecurityIcon,
  SettingsOutlined as GeneralIcon,
  DescriptionOutlined as DocumentIcon,
  IntegrationInstructionsOutlined as IntegrationIcon,
  AddOutlined as AddIcon,
  DeleteOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  PersonOutlined as PersonIcon,
  EmailOutlined as EmailIcon,
  WarningAmberOutlined as WarningIcon,
  InfoOutlined as InfoIcon,
  SaveOutlined as SaveIcon,
} from '@mui/icons-material';

const NOTIFICATION_LEAD_TIMES = [
  { value: 90, label: '90 days' },
  { value: 60, label: '60 days' },
  { value: 30, label: '30 days' },
  { value: 14, label: '14 days' },
  { value: 7, label: '7 days' },
  { value: 1, label: '1 day' },
];

const RECIPIENT_TYPES = [
  { value: 'primary', label: 'Primary', color: 'primary' },
  { value: 'cc', label: 'CC', color: 'default' },
  { value: 'escalation', label: 'Escalation', color: 'warning' },
];

const DEFAULT_DOCUMENT_SETTINGS = {
  mandatoryTypeCodes: [...DEFAULT_MANDATORY_TYPE_CODES],
  directorMandatoryTypeCodes: [...DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES],
  autoArchive: true,
  autoArchiveDays: 30,
  retentionYears: 7,
};

const RETENTION_YEAR_OPTIONS = [
  { value: 1, label: '1 year' },
  { value: 3, label: '3 years' },
  { value: 5, label: '5 years' },
  { value: 7, label: '7 years' },
  { value: -1, label: 'Forever' },
];

const DEFAULT_INTEGRATION_SETTINGS = {
  apiEnabled: false,
  webhooks: [],
};

const DEFAULT_NOTIFICATION_SETTINGS = {
  leadTimes: [90, 60, 30, 7],
  escalationEnabled: true,
  escalationDays: 7,
  digestMode: 'individual',
};

const WEBHOOK_EVENT_OPTIONS = [
  { value: 'all', label: 'All events' },
  { value: 'document.uploaded', label: 'Document uploaded' },
  { value: 'document.expired', label: 'Document expired' },
  { value: 'director.added', label: 'Director added' },
  { value: 'officer.updated', label: 'Officer updated' },
  { value: 'committee.meeting.scheduled', label: 'Meeting scheduled' },
];

const SettingsTab = ({ companyId, company }) => {
  const navigate = useNavigate();
  const { items: companyDocumentTypes } = useLookups('COMPANY_DOCUMENT_TYPE');
  const { items: directorDocumentTypes } = useLookups('DIRECTOR_DOCUMENT_TYPE');
  const activeDocumentTypeOptions = useMemo(
    () => (companyDocumentTypes || []).filter((item) => item.isActive !== false),
    [companyDocumentTypes]
  );
  const activeDirectorDocumentTypeOptions = useMemo(
    () => (directorDocumentTypes || []).filter((item) => item.isActive !== false),
    [directorDocumentTypes]
  );
  const [activeSection, setActiveSection] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [addRecipientOpen, setAddRecipientOpen] = useState(false);
  const [editRecipient, setEditRecipient] = useState(null);
  const [addMandatoryTypeOpen, setAddMandatoryTypeOpen] = useState(false);
  const [selectedMandatoryTypes, setSelectedMandatoryTypes] = useState([]);
  const [addDirectorMandatoryTypeOpen, setAddDirectorMandatoryTypeOpen] = useState(false);
  const [selectedDirectorMandatoryTypes, setSelectedDirectorMandatoryTypes] = useState([]);
  const [addWebhookOpen, setAddWebhookOpen] = useState(false);
  const [webhookForm, setWebhookForm] = useState({ url: '', label: '', events: ['all'] });
  const [hasChanges, setHasChanges] = useState(false);
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATION_SETTINGS);

  // General settings state
  const [generalSettings, setGeneralSettings] = useState({
    displayCurrency: 'AED',
    fiscalYearStart: 'January',
    timezone: 'Asia/Dubai',
  });

  const [documentSettings, setDocumentSettings] = useState(DEFAULT_DOCUMENT_SETTINGS);
  const [integrationSettings, setIntegrationSettings] = useState(DEFAULT_INTEGRATION_SETTINGS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await csAdminApi.getUsers();
        const users = (res.data?.data?.users || []).map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.roleLabel || user.role || 'User',
        }));
        if (!cancelled) {
          setAvailableUsers(users);
        }
      } catch (err) {
        console.error('Failed to load users for recipients:', err);
        if (!cancelled) setAvailableUsers([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!companyId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await csAdminApi.getCompanySettings(companyId);
        const data = res.data?.data || {};
        const general = data.general || {};
        const documents = data.documents || {};
        const integrations = data.integrations || {};
        const notifications = data.notifications || {};

        if (!cancelled) {
          setGeneralSettings({
            displayCurrency: general.currency || 'AED',
            fiscalYearStart: general.fiscalYearEnd || 'January',
            timezone: general.timezone || 'Asia/Dubai',
          });
          setDocumentSettings({
            mandatoryTypeCodes: resolveMandatoryTypeCodes(documents, activeDocumentTypeOptions),
            directorMandatoryTypeCodes: resolveDirectorMandatoryTypeCodes(
              documents,
              activeDirectorDocumentTypeOptions
            ),
            autoArchive: documents.autoArchive !== false,
            autoArchiveDays: documents.autoArchiveDays || DEFAULT_DOCUMENT_SETTINGS.autoArchiveDays,
            retentionYears: documents.retentionYears ?? DEFAULT_DOCUMENT_SETTINGS.retentionYears,
          });
          setIntegrationSettings({
            apiEnabled: integrations.apiEnabled === true,
            webhooks: Array.isArray(integrations.webhooks)
              ? integrations.webhooks.map((webhook, index) => ({
                id: webhook.id || `wh-${index + 1}`,
                url: webhook.url || '',
                label: webhook.label || '',
                events: Array.isArray(webhook.events) && webhook.events.length ? webhook.events : ['all'],
                enabled: webhook.enabled !== false,
              }))
              : [],
          });
          setNotificationSettings({
            leadTimes: Array.isArray(notifications.leadTimes) && notifications.leadTimes.length
              ? notifications.leadTimes
              : DEFAULT_NOTIFICATION_SETTINGS.leadTimes,
            escalationEnabled: notifications.escalationEnabled !== false,
            escalationDays: notifications.escalationDays ?? DEFAULT_NOTIFICATION_SETTINGS.escalationDays,
            digestMode: notifications.digestMode || DEFAULT_NOTIFICATION_SETTINGS.digestMode,
          });
          setRecipients(
            Array.isArray(data.recipients) && data.recipients.length
              ? data.recipients.map((recipient, index) => ({
                id: recipient.id || index + 1,
                name: recipient.name,
                email: recipient.email,
                type: index === 0 ? 'primary' : 'cc',
                role: 'Recipient',
                channels: {
                  email: recipient.notifyEmail !== false,
                },
                isDefault: index === 0,
              }))
              : []
          );
          setHasChanges(false);
        }
      } catch (err) {
        console.error('Failed to load company settings:', err);
        if (!cancelled) {
          setRecipients([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, activeDocumentTypeOptions, activeDirectorDocumentTypeOptions]);

  const handleLeadTimeToggle = (value) => {
    setNotificationSettings(prev => {
      const newLeadTimes = prev.leadTimes.includes(value)
        ? prev.leadTimes.filter(t => t !== value)
        : [...prev.leadTimes, value].sort((a, b) => b - a);
      return { ...prev, leadTimes: newLeadTimes };
    });
    setHasChanges(true);
  };

  const handleRecipientChannelToggle = (recipientId, channel) => {
    setRecipients(prev => prev.map(r => {
      if (r.id === recipientId) {
        return {
          ...r,
          channels: { ...r.channels, [channel]: !r.channels[channel] }
        };
      }
      return r;
    }));
    setHasChanges(true);
  };

  const handleDeleteRecipient = (recipientId) => {
    setRecipients(prev => prev.filter(r => r.id !== recipientId));
    setHasChanges(true);
  };

  const handleDocumentSettingChange = (field, value) => {
    setDocumentSettings((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleRemoveMandatoryType = (code) => {
    setDocumentSettings((prev) => ({
      ...prev,
      mandatoryTypeCodes: prev.mandatoryTypeCodes.filter((c) => c !== code),
    }));
    setHasChanges(true);
  };

  const handleOpenAddMandatoryTypes = () => {
    const selected = activeDocumentTypeOptions.filter((item) =>
      documentSettings.mandatoryTypeCodes.includes(item.code)
    );
    setSelectedMandatoryTypes(selected);
    setAddMandatoryTypeOpen(true);
  };

  const handleSaveMandatoryTypes = () => {
    const codes = selectedMandatoryTypes
      .map((item) => item.code)
      .filter(Boolean);
    setDocumentSettings((prev) => ({
      ...prev,
      mandatoryTypeCodes: codes,
    }));
    setAddMandatoryTypeOpen(false);
    setHasChanges(true);
  };

  const handleRemoveDirectorMandatoryType = (code) => {
    setDocumentSettings((prev) => ({
      ...prev,
      directorMandatoryTypeCodes: prev.directorMandatoryTypeCodes.filter((c) => c !== code),
    }));
    setHasChanges(true);
  };

  const handleOpenAddDirectorMandatoryTypes = () => {
    const selected = activeDirectorDocumentTypeOptions.filter((item) =>
      documentSettings.directorMandatoryTypeCodes.includes(item.code)
    );
    setSelectedDirectorMandatoryTypes(selected);
    setAddDirectorMandatoryTypeOpen(true);
  };

  const handleSaveDirectorMandatoryTypes = () => {
    const codes = selectedDirectorMandatoryTypes
      .map((item) => item.code)
      .filter(Boolean);
    setDocumentSettings((prev) => ({
      ...prev,
      directorMandatoryTypeCodes: codes,
    }));
    setAddDirectorMandatoryTypeOpen(false);
    setHasChanges(true);
  };

  const handleIntegrationSettingChange = (field, value) => {
    setIntegrationSettings((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleRemoveWebhook = (webhookId) => {
    setIntegrationSettings((prev) => ({
      ...prev,
      webhooks: prev.webhooks.filter((webhook) => webhook.id !== webhookId),
    }));
    setHasChanges(true);
  };

  const handleToggleWebhook = (webhookId) => {
    setIntegrationSettings((prev) => ({
      ...prev,
      webhooks: prev.webhooks.map((webhook) =>
        webhook.id === webhookId ? { ...webhook, enabled: !webhook.enabled } : webhook
      ),
    }));
    setHasChanges(true);
  };

  const handleAddWebhook = () => {
    const url = webhookForm.url.trim();
    if (!url) return;
    setIntegrationSettings((prev) => ({
      ...prev,
      webhooks: [
        ...prev.webhooks,
        {
          id: `wh-${Date.now()}`,
          url,
          label: webhookForm.label.trim(),
          events: webhookForm.events.length ? webhookForm.events : ['all'],
          enabled: true,
        },
      ],
    }));
    setWebhookForm({ url: '', label: '', events: ['all'] });
    setAddWebhookOpen(false);
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    try {
      const payload = {
        general: {
          currency: generalSettings.displayCurrency,
          fiscalYearEnd: generalSettings.fiscalYearStart,
          timezone: generalSettings.timezone,
        },
        documents: {
          mandatoryTypeCodes: documentSettings.mandatoryTypeCodes,
          directorMandatoryTypeCodes: documentSettings.directorMandatoryTypeCodes,
          autoArchive: documentSettings.autoArchive,
          autoArchiveDays: documentSettings.autoArchiveDays,
          retentionYears: documentSettings.retentionYears,
        },
        integrations: {
          apiEnabled: integrationSettings.apiEnabled,
          webhooks: integrationSettings.webhooks.map((webhook) => ({
            id: webhook.id,
            url: webhook.url,
            label: webhook.label || null,
            events: webhook.events,
            enabled: webhook.enabled !== false,
          })),
        },
        notifications: {
          leadTimes: notificationSettings.leadTimes,
          escalationEnabled: notificationSettings.escalationEnabled,
          escalationDays: notificationSettings.escalationDays,
          digestMode: notificationSettings.digestMode,
        },
        recipients: recipients.map((r) => ({
          name: r.name,
          email: r.email,
          notifyEmail: r.channels?.email !== false,
          notifySms: false,
          notifyInApp: false,
        })),
      };
      await csAdminApi.updateCompanySettings(companyId, payload);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
  };

  const sections = [
    { icon: <GeneralIcon />, label: 'General' },
    { icon: <NotificationsIcon />, label: 'Notifications' },
    { icon: <DocumentIcon />, label: 'Documents' },
    { icon: <SecurityIcon />, label: 'Permissions' },
    { icon: <IntegrationIcon />, label: 'Integrations' },
  ];

  return (
    <Paper sx={{ p: 0, display: 'flex', minHeight: 600 }}>
      {/* Settings Navigation */}
      <Box sx={{ 
        width: 200, 
        borderRight: 1, 
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Settings
          </Typography>
        </Box>
        <List sx={{ p: 1 }}>
          {sections.map((section, index) => (
            <ListItem
              key={section.label}
              button
              selected={activeSection === index}
              onClick={() => setActiveSection(index)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '& .MuiListItemIcon-root': { color: 'white' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {section.icon}
              </ListItemIcon>
              <ListItemText 
                primary={section.label} 
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Settings Content */}
      <Box sx={{ flex: 1, p: 3 }}>
        {/* Save Button - Fixed at top */}
        {hasChanges && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="primary" 
                variant="contained" 
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
              >
                Save Changes
              </Button>
            }
          >
            You have unsaved changes
          </Alert>
        )}

        {/* General Settings */}
        {activeSection === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>General Settings</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Configure general company preferences
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Display Currency</InputLabel>
                  <Select
                    value={generalSettings.displayCurrency}
                    label="Display Currency"
                    onChange={(e) => {
                      setGeneralSettings(prev => ({ ...prev, displayCurrency: e.target.value }));
                      setHasChanges(true);
                    }}
                  >
                    <MenuItem value="AED">AED - UAE Dirham</MenuItem>
                    <MenuItem value="USD">USD - US Dollar</MenuItem>
                    <MenuItem value="EUR">EUR - Euro</MenuItem>
                    <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Fiscal Year Start</InputLabel>
                  <Select
                    value={generalSettings.fiscalYearStart}
                    label="Fiscal Year Start"
                    onChange={(e) => {
                      setGeneralSettings(prev => ({ ...prev, fiscalYearStart: e.target.value }));
                      setHasChanges(true);
                    }}
                  >
                    <MenuItem value="January">January</MenuItem>
                    <MenuItem value="April">April</MenuItem>
                    <MenuItem value="July">July</MenuItem>
                    <MenuItem value="October">October</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Time Zone</InputLabel>
                  <Select
                    value={generalSettings.timezone}
                    label="Time Zone"
                    onChange={(e) => {
                      setGeneralSettings(prev => ({ ...prev, timezone: e.target.value }));
                      setHasChanges(true);
                    }}
                  >
                    <MenuItem value="Asia/Dubai">Asia/Dubai (GMT+4)</MenuItem>
                    <MenuItem value="Asia/Riyadh">Asia/Riyadh (GMT+3)</MenuItem>
                    <MenuItem value="Africa/Cairo">Africa/Cairo (GMT+2)</MenuItem>
                    <MenuItem value="Asia/Karachi">Asia/Karachi (GMT+5)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Notification Settings */}
        {activeSection === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Notification Settings</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Configure who receives notifications and when
            </Typography>

            {/* Recipients Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Notification Recipients
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<AddIcon />}
                  onClick={() => setAddRecipientOpen(true)}
                >
                  Add Recipient
                </Button>
              </Box>

              <Card variant="outlined">
                <List sx={{ p: 0 }}>
                  {recipients.map((recipient, index) => (
                    <React.Fragment key={recipient.id}>
                      {index > 0 && <Divider />}
                      <ListItem sx={{ py: 2 }}>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                            {recipient.name.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">{recipient.name}</Typography>
                              <Chip 
                                label={recipient.type.toUpperCase()} 
                                size="small"
                                color={RECIPIENT_TYPES.find(t => t.value === recipient.type)?.color || 'default'}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              {recipient.isDefault && (
                                <Chip 
                                  label="DEFAULT" 
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                {recipient.email} • {recipient.role}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox 
                                      size="small" 
                                      checked={recipient.channels.email}
                                      onChange={() => handleRecipientChannelToggle(recipient.id, 'email')}
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <EmailIcon sx={{ fontSize: 16 }} />
                                      <Typography variant="body2">Email</Typography>
                                    </Box>
                                  }
                                />
                              </Box>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            size="small" 
                            onClick={() => setEditRecipient(recipient)}
                            title="Edit recipient"
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {!recipient.isDefault && (
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteRecipient(recipient.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Card>
            </Box>

            {/* Timing Settings */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Notification Timing
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Select when to send expiry notifications
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {NOTIFICATION_LEAD_TIMES.map(({ value, label }) => (
                  <Chip
                    key={value}
                    label={label}
                    onClick={() => handleLeadTimeToggle(value)}
                    color={notificationSettings.leadTimes.includes(value) ? 'primary' : 'default'}
                    variant={notificationSettings.leadTimes.includes(value) ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Notification Mode</InputLabel>
                <Select
                  value={notificationSettings.digestMode}
                  label="Notification Mode"
                  onChange={(e) => {
                    setNotificationSettings(prev => ({ ...prev, digestMode: e.target.value }));
                    setHasChanges(true);
                  }}
                >
                  <MenuItem value="individual">Individual (per document)</MenuItem>
                  <MenuItem value="daily">Daily Digest</MenuItem>
                  <MenuItem value="weekly">Weekly Digest</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Escalation Settings */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Escalation Rules
              </Typography>

              <Card variant="outlined" sx={{ p: 2, bgcolor: 'warning.50' }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={notificationSettings.escalationEnabled}
                      onChange={(e) => {
                        setNotificationSettings(prev => ({ ...prev, escalationEnabled: e.target.checked }));
                        setHasChanges(true);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        Escalate to Group Company Secretary
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        If no action is taken on overdue documents
                      </Typography>
                    </Box>
                  }
                />
                {notificationSettings.escalationEnabled && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">Escalate after</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={notificationSettings.escalationDays}
                      onChange={(e) => {
                        setNotificationSettings(prev => ({ ...prev, escalationDays: parseInt(e.target.value) || 7 }));
                        setHasChanges(true);
                      }}
                      sx={{ width: 80 }}
                      inputProps={{ min: 1, max: 30 }}
                    />
                    <Typography variant="body2">days overdue</Typography>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        )}

        {/* Documents Settings */}
        {activeSection === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Document Settings</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Choose mandatory document types from the master lists in Lookups (company and director types).
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Mandatory Company Document Types
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Documents that must be uploaded for company compliance
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => navigate('/lookups')}>
                      Manage types
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {documentSettings.mandatoryTypeCodes.map((code) => (
                      <Chip
                        key={code}
                        label={lookupLabel(activeDocumentTypeOptions, code)}
                        onDelete={() => handleRemoveMandatoryType(code)}
                      />
                    ))}
                    <Chip
                      label="+ Add from lookup"
                      variant="outlined"
                      onClick={handleOpenAddMandatoryTypes}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                  {documentSettings.mandatoryTypeCodes.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      No mandatory types selected. The Documents tab checklist will be empty until you add types.
                    </Alert>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Mandatory Director Document Types
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Required when onboarding directors and on the director Documents tab
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => navigate('/lookups')}>
                      Manage types
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {documentSettings.directorMandatoryTypeCodes.map((code) => (
                      <Chip
                        key={code}
                        label={lookupLabel(activeDirectorDocumentTypeOptions, code)}
                        onDelete={() => handleRemoveDirectorMandatoryType(code)}
                      />
                    ))}
                    <Chip
                      label="+ Add from lookup"
                      variant="outlined"
                      onClick={handleOpenAddDirectorMandatoryTypes}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                  {documentSettings.directorMandatoryTypeCodes.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      No mandatory director types selected. Onboarding and director document checklists will be empty.
                    </Alert>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Auto-Archive
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={documentSettings.autoArchive}
                        onChange={(e) => handleDocumentSettingChange('autoArchive', e.target.checked)}
                      />
                    }
                    label="Archive expired documents automatically"
                  />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Documents will be moved to archive {documentSettings.autoArchiveDays} days after expiry
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Retention Policy
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Keep archived documents for</InputLabel>
                    <Select
                      value={documentSettings.retentionYears}
                      label="Keep archived documents for"
                      onChange={(e) => handleDocumentSettingChange('retentionYears', e.target.value)}
                    >
                      {RETENTION_YEAR_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Permissions Section - Placeholder for detailed component */}
        {activeSection === 3 && (
          <PermissionsSection companyId={companyId} company={company} />
        )}

        {/* Integrations Settings */}
        {activeSection === 4 && (
          <Box>
            <Typography variant="h6" gutterBottom>Integration Settings</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Configure external system connections
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              API integrations require administrator approval. Webhook URLs are called when configured events occur.
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        API Access
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Enable API access for external integrations
                      </Typography>
                    </Box>
                    <Switch
                      checked={integrationSettings.apiEnabled}
                      onChange={(e) => handleIntegrationSettingChange('apiEnabled', e.target.checked)}
                    />
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Webhooks
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Receive HTTP notifications when events occur
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setAddWebhookOpen(true)}
                      size="small"
                    >
                      Add Webhook
                    </Button>
                  </Box>

                  {integrationSettings.webhooks.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No webhooks configured.
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {integrationSettings.webhooks.map((webhook) => (
                        <ListItem
                          key={webhook.id}
                          divider
                          sx={{ px: 0 }}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => handleRemoveWebhook(webhook.id)}>
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Switch
                              size="small"
                              checked={webhook.enabled}
                              onChange={() => handleToggleWebhook(webhook.id)}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={webhook.label || webhook.url}
                            secondary={
                              <>
                                <Typography variant="caption" component="span" display="block">
                                  {webhook.url}
                                </Typography>
                                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(webhook.events || ['all']).map((event) => (
                                    <Chip key={event} label={event} size="small" variant="outlined" />
                                  ))}
                                </Box>
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Add Recipient Dialog */}
      <AddRecipientDialog
        open={addRecipientOpen}
        onClose={() => setAddRecipientOpen(false)}
        onAdd={(newRecipient) => {
          setRecipients(prev => [...prev, { ...newRecipient, id: Date.now() }]);
          setAddRecipientOpen(false);
          setHasChanges(true);
        }}
        existingRecipients={recipients}
        availableUsers={availableUsers}
      />

      <EditRecipientDialog
        open={Boolean(editRecipient)}
        recipient={editRecipient}
        onClose={() => setEditRecipient(null)}
        onSave={(updated) => {
          setRecipients((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
          setEditRecipient(null);
          setHasChanges(true);
        }}
      />

      <Dialog
        open={addMandatoryTypeOpen}
        onClose={() => setAddMandatoryTypeOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select mandatory document types</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Types come from Lookups → Company Document Types. IT Admin can add new types there.
          </Typography>
          <Autocomplete
            multiple
            options={activeDocumentTypeOptions}
            value={selectedMandatoryTypes}
            onChange={(_, value) => setSelectedMandatoryTypes(value)}
            getOptionLabel={(option) => option.label || option.code}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderInput={(params) => (
              <TextField {...params} label="Document types" placeholder="Search types..." />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.code}
                  label={option.label || option.code}
                  size="small"
                />
              ))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMandatoryTypeOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMandatoryTypes}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addDirectorMandatoryTypeOpen}
        onClose={() => setAddDirectorMandatoryTypeOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select mandatory director document types</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Types come from Lookups → Director Document Types.
          </Typography>
          <Autocomplete
            multiple
            options={activeDirectorDocumentTypeOptions}
            value={selectedDirectorMandatoryTypes}
            onChange={(_, value) => setSelectedDirectorMandatoryTypes(value)}
            getOptionLabel={(option) => option.label || option.code}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderInput={(params) => (
              <TextField {...params} label="Director document types" placeholder="Search types..." />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.code}
                  label={option.label || option.code}
                  size="small"
                />
              ))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDirectorMandatoryTypeOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDirectorMandatoryTypes}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addWebhookOpen}
        onClose={() => {
          setAddWebhookOpen(false);
          setWebhookForm({ url: '', label: '', events: ['all'] });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Webhook</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            required
            label="Webhook URL"
            placeholder="https://example.com/webhooks/company-events"
            value={webhookForm.url}
            onChange={(e) => setWebhookForm((prev) => ({ ...prev, url: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Label (optional)"
            value={webhookForm.label}
            onChange={(e) => setWebhookForm((prev) => ({ ...prev, label: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Events</InputLabel>
            <Select
              multiple
              value={webhookForm.events}
              label="Events"
              onChange={(e) => setWebhookForm((prev) => ({
                ...prev,
                events: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
              }))}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {WEBHOOK_EVENT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddWebhookOpen(false);
            setWebhookForm({ url: '', label: '', events: ['all'] });
          }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddWebhook} disabled={!webhookForm.url.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const PERMISSION_TEMPLATES = [
  { name: 'Full Admin', description: 'All sections, all actions', color: 'error' },
  { name: 'Editor', description: 'View + Edit all sections', color: 'warning' },
  { name: 'Viewer', description: 'View only, no edits', color: 'info' },
  { name: 'Auditor', description: 'View only, with reports', color: 'success' },
];

// Permissions Section Component
const PermissionsSection = ({ companyId }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState(PERMISSION_TEMPLATES);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editUserPermissions, setEditUserPermissions] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [usersRes, permsRes] = await Promise.all([
          csAdminApi.getUsers(),
          csAdminApi.getCompanyPermissions(companyId),
        ]);
        const allUsers = usersRes.data?.data?.users || [];
        const permData = permsRes.data?.data || {};
        const permUsers = permData.users || [];
        const permPortalAccounts = permData.portalAccounts || [];
        const permByKey = Object.fromEntries([
          ...permUsers.map((u) => [`user:${u.id}`, u]),
          ...permPortalAccounts.map((u) => [`portal_account:${u.id}`, u]),
        ]);

        const mapPrincipalRow = (principal, principalType) => {
          const perm = permByKey[`${principalType}:${principal.id}`];
          return {
            id: principal.id,
            principalType,
            name: principal.name,
            email: principal.email,
            role: principal.role || principal.roleLabel || 'User',
            permissions: perm?.accessLabel || principal.roleLabel || principal.role || '—',
            avatar: (principal.name || '?')
              .split(' ')
              .map((part) => part[0])
              .join('')
              .slice(0, 2)
              .toUpperCase(),
          };
        };

        const scopedUsers = allUsers
          .filter((user) =>
            (user.assignedCompanies || []).map(String).includes(String(companyId))
            || String(user.company_id) === String(companyId)
            || ['IT_ADMIN', 'GROUP_SECRETARY', 'SECRETARIAL_ADMIN'].includes(
              String(user.role || '').toUpperCase()
            ))
          .map((user) => mapPrincipalRow(user, 'user'));

        const portalRows = permPortalAccounts.map((account) => mapPrincipalRow(account, 'portal_account'));
        const merged = [...scopedUsers, ...portalRows];

        if (!cancelled) {
          setUsers(merged);
          if (permData.templates?.length) {
            setTemplates(permData.templates.map((t) => ({
              name: t.name,
              description: t.description,
              color: t.color || 'primary',
            })));
          }
        }
      } catch (err) {
        console.error('Failed to load company permissions users:', err);
        if (!cancelled) {
          setLoadError('Failed to load users with access');
          setUsers([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Access & Permissions</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Manage who has access to this company and what they can do
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Admin user roles are managed in{' '}
        <Button size="small" onClick={() => navigate('/users-permissions')}>
          Users &amp; Permissions
        </Button>
        . Company secretary accounts are managed in{' '}
        <Button size="small" onClick={() => navigate('/company-secretaries')}>
          Company Secretaries
        </Button>
        . Click Edit on a user to save per-company permission overrides.
      </Alert>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      {/* Users with Access */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Users with Access
          </Typography>
        </Box>

        <Card variant="outlined">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                No users are currently assigned to this company. Assign company secretaries and admin users from{' '}
                <Button size="small" onClick={() => navigate('/users-permissions')}>
                  Users &amp; Permissions
                </Button>
                .
              </Alert>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {users.map((user, index) => (
                <React.Fragment key={`${user.principalType || 'user'}:${user.id}`}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                        {user.avatar}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">{user.name}</Typography>
                          <Chip
                            label={user.role}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      )}
                      secondary={(
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="textSecondary">
                            {user.email}
                          </Typography>
                          <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                            Access: {user.permissions}
                          </Typography>
                        </Box>
                      )}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        onClick={() => setEditUserPermissions(user)}
                        sx={{ mr: 0 }}
                      >
                        Edit
                      </Button>
                                          </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Card>
      </Box>

      {/* Permission Templates */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Permission Templates
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {templates.map((template) => (
            <Grid item xs={6} md={3} key={template.name}>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: 'default',
                }}
              >
                <Chip
                  label={template.name}
                  size="small"
                  color={template.color}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="textSecondary">
                  {template.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {editUserPermissions && (
        <EditPermissionsDialog
          open={Boolean(editUserPermissions)}
          companyId={companyId}
          user={editUserPermissions}
          onClose={() => setEditUserPermissions(null)}
          onSave={async (payload) => {
            try {
              if (editUserPermissions.principalType === 'portal_account') {
                  await csAdminApi.updateCompanyPortalAccountPermissions(
                    companyId,
                    editUserPermissions.id,
                    { useCustom: true, matrix: payload.matrix }
                  );
                } else {
                  await csAdminApi.updateCompanyUserPermissions(companyId, editUserPermissions.id, {
                    useCustom: true,
                    matrix: payload.matrix,
                  });
                }
                enqueueSnackbar('Company permissions saved', { variant: 'success' });
              setUsers((prev) => prev.map((user) => (
                user.id === editUserPermissions.id
                && (user.principalType || 'user') === (editUserPermissions.principalType || 'user')
                  ? { ...user, permissions: 'Custom' }
                  : user
              )));
            } catch (err) {
              console.error('Save company permissions:', err);
              enqueueSnackbar('Failed to save permissions', { variant: 'error' });
              return;
            }
            setEditUserPermissions(null);
          }}
        />
      )}
    </Box>
  );
};

// Add Recipient Dialog
const AddRecipientDialog = ({ open, onClose, onAdd, existingRecipients, availableUsers }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [externalEmail, setExternalEmail] = useState('');
  const [recipientType, setRecipientType] = useState('cc');
  const [channels, setChannels] = useState({ email: true });
  const [useExternal, setUseExternal] = useState(false);

  const handleAdd = () => {
    if (useExternal && externalEmail) {
      onAdd({
        name: externalEmail.split('@')[0],
        email: externalEmail,
        type: recipientType,
        role: 'External',
        channels,
        isDefault: false,
      });
    } else if (selectedUser) {
      onAdd({
        name: selectedUser.name,
        email: selectedUser.email,
        type: recipientType,
        role: selectedUser.role,
        channels,
        isDefault: false,
      });
    }
    // Reset form
    setSelectedUser(null);
    setExternalEmail('');
    setRecipientType('cc');
    setChannels({ email: true });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Notification Recipient</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={useExternal} 
                onChange={(e) => setUseExternal(e.target.checked)}
              />
            }
            label="Add external email"
            sx={{ mb: 2 }}
          />

          {useExternal ? (
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={externalEmail}
              onChange={(e) => setExternalEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
          ) : (
            <Autocomplete
              options={availableUsers.filter(u => !existingRecipients.find(r => r.email === u.email))}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={selectedUser}
              onChange={(e, newValue) => setSelectedUser(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select User" />
              )}
              sx={{ mb: 2 }}
            />
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Recipient Type</InputLabel>
            <Select
              value={recipientType}
              label="Recipient Type"
              onChange={(e) => setRecipientType(e.target.value)}
            >
              {RECIPIENT_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={channels.email}
                onChange={(e) => setChannels({ email: e.target.checked })}
              />
            }
            label="Send email notifications"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleAdd}
          disabled={useExternal ? !externalEmail.trim() : !selectedUser}
        >
          Add Recipient
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Recipient Dialog
const EditRecipientDialog = ({ open, recipient, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [recipientType, setRecipientType] = useState('cc');
  const [emailEnabled, setEmailEnabled] = useState(true);

  useEffect(() => {
    if (!recipient) return;
    setName(recipient.name || '');
    setEmail(recipient.email || '');
    setRecipientType(recipient.type || 'cc');
    setEmailEnabled(recipient.channels?.email !== false);
  }, [recipient]);

  const handleSave = () => {
    if (!recipient || !email.trim()) return;
    onSave({
      ...recipient,
      name: name.trim() || email.split('@')[0],
      email: email.trim(),
      type: recipientType,
      channels: { email: emailEnabled },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Notification Recipient</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <FormControl fullWidth>
            <InputLabel>Recipient Type</InputLabel>
            <Select
              value={recipientType}
              label="Recipient Type"
              onChange={(e) => setRecipientType(e.target.value)}
              disabled={recipient?.isDefault}
            >
              {RECIPIENT_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {recipient?.isDefault && (
            <Typography variant="caption" color="textSecondary">
              Default recipient stays primary; change name and email below.
            </Typography>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
              />
            }
            label="Send email notifications"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!email.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Permissions Dialog
const EditPermissionsDialog = ({ open, user, companyId, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({
    overview: { view: true, edit: false },
    documents: { view: true, edit: true, delete: false, upload: true },
    directors: { view: true, edit: false, delete: false },
    committees: { view: true, edit: false, delete: false },
    shareholders: { view: true, edit: false, delete: false },
    officers: { view: true, edit: true, delete: false },
    settings: { view: false, edit: false },
    auditLog: { view: true },
  });

  useEffect(() => {
    if (!open || !user || !companyId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await csAdminApi.getCompanyPermissions(companyId);
        const principalType = user.principalType || 'user';
        const list = principalType === 'portal_account'
          ? (res.data?.data?.portalAccounts || [])
          : (res.data?.data?.users || []);
        const permUser = list.find((u) => String(u.id) === String(user.id));
        if (!cancelled && permUser?.effective) {
          setPermissions(permUser.effective);
        }
      } catch (err) {
        console.error('Load company user permissions:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, user, companyId]);

  const sections = [
    { key: 'overview', label: 'Overview', actions: ['view', 'edit'] },
    { key: 'documents', label: 'Documents', actions: ['view', 'edit', 'delete', 'upload'] },
    { key: 'directors', label: 'Directors', actions: ['view', 'edit', 'delete'] },
    { key: 'committees', label: 'Committees', actions: ['view', 'edit', 'delete'] },
    { key: 'shareholders', label: 'Shareholders', actions: ['view', 'edit', 'delete'] },
    { key: 'officers', label: 'Officers', actions: ['view', 'edit', 'delete'] },
    { key: 'settings', label: 'Settings', actions: ['view', 'edit'] },
    { key: 'auditLog', label: 'Audit Log', actions: ['view'] },
  ];

  const handlePermissionChange = (section, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [section]: { ...prev[section], [action]: value }
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Permissions - {user?.name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
          <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Configure granular permissions for each section
          </Alert>

          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid #e0e0e0' }}>Section</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid #e0e0e0' }}>View</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid #e0e0e0' }}>Edit</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid #e0e0e0' }}>Delete</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid #e0e0e0' }}>Upload</th>
                </tr>
              </thead>
              <tbody>
                {sections.map(section => (
                  <tr key={section.key}>
                    <td style={{ padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" fontWeight={500}>{section.label}</Typography>
                    </td>
                    {['view', 'edit', 'delete', 'upload'].map(action => (
                      <td key={action} style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid #e0e0e0' }}>
                        {section.actions.includes(action) ? (
                          <Checkbox
                            size="small"
                            checked={permissions[section.key]?.[action] || false}
                            onChange={(e) => handlePermissionChange(section.key, action, e.target.checked)}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave({ matrix: permissions });
            } finally {
              setSaving(false);
            }
          }}
        >
          Save Permissions
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsTab;
